import {
  buildMemberCreatePayload,
  validateMemberCreatePayload,
} from "../../member/createPayload";
import { simpleCommands } from "./simpleCommands";
import { scheduledCommands } from "./scheduledCommands";
import { saleCommand } from "./saleCommand";

export const CREATE_COMMAND_TYPES = [
  "member",
  "package",
  "lead",
  "product",
  "instructor",
  "staff",
  "workout",
  "followup",
  "gate",
  "court",
  "appointment",
  "class",
  "workshop",
  "sale",
];

export const SUPPORTED_CREATE_COMMAND_TYPES = [
  "member",
  "package",
  "lead",
  "product",
  "instructor",
  "staff",
  "workout",
  "followup",
  "gate",
  "court",
  "appointment",
  "class",
  "workshop",
  "sale",
];

export const NEEDS_FORM_CREATE_COMMAND_TYPES = CREATE_COMMAND_TYPES.filter(
  (type) => !SUPPORTED_CREATE_COMMAND_TYPES.includes(type)
);

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const missingKeys = (fields, keys) =>
  keys.filter((key) => !hasValue(fields[key]));

const needsForm = (type, code, message, details = {}) => ({
  outcome: "needs_form",
  type,
  reason: {
    code,
    message,
    ...details,
  },
});

const ready = (type, endpoint, payload) => ({
  outcome: "ready",
  type,
  endpoint,
  method: "post",
  payload,
});

const required = (type, fields, keys) => {
  const missing = missingKeys(fields, keys);
  return missing.length
    ? needsForm(
        type,
        "missing_required_fields",
        `Missing ${missing.join(", ")}.`,
        { fields: missing }
      )
    : null;
};

const lookupId = (value) => {
  if (!value || typeof value !== "object") return value;
  if (hasValue(value.value)) return value.value;
  return value.id;
};

const memberCommand = {
  preparePayload(fields = {}, context = {}) {
    const payload = buildMemberCreatePayload(fields, {
      country: context.country,
      requireCard: context.requireCard,
    });
    const errors = validateMemberCreatePayload(payload);
    if (Object.keys(errors).length) {
      return needsForm(
        "member",
        "invalid_member_payload",
        Object.values(errors).join(". ") ||
          "The member has invalid or missing details.",
        { errors }
      );
    }
    return ready("member", "member", payload);
  },
};

const staffCommand = {
  preparePayload(fields = {}) {
    const invalid = required("staff", fields, [
      "name",
      "email",
      "phone",
      "permissions",
    ]);
    if (invalid) return invalid;
    if (!Array.isArray(fields.permissions) || !fields.permissions.length) {
      return needsForm(
        "staff",
        "missing_required_fields",
        "The manual staff form requires at least one permission.",
        { fields: ["permissions"] }
      );
    }
    return ready("staff", "staff", {
      ...fields,
      permissions: fields.permissions.map(String),
      title: hasValue(fields.title) ? fields.title : "",
    });
  },
};

const workoutCommand = {
  preparePayload(fields = {}) {
    const invalid = required("workout", fields, [
      "name",
      "type",
      "duration",
      "instructions",
    ]);
    if (invalid) return invalid;
    if (!Array.isArray(fields.instructions)) {
      return needsForm(
        "workout",
        "invalid_instructions",
        "Workout instructions must retain the manual form's array shape."
      );
    }
    return ready("workout", "wod", { ...fields });
  },
};

const followupCommand = {
  preparePayload(fields = {}) {
    const invalid = required("followup", fields, [
      "name",
      "followupPeriod",
      "notifyOnMissedTasksAfter",
      "followupOn",
    ]);
    if (invalid) return invalid;
    if (!Array.isArray(fields.followupOn) || !fields.followupOn.length) {
      return needsForm(
        "followup",
        "missing_required_fields",
        "The manual follow-up form requires at least one follow-up option.",
        { fields: ["followupOn"] }
      );
    }
    return ready("followup", "followupProgram", {
      name: fields.name,
      followupPeriod: fields.followupPeriod,
      notifyOnMissedTasksAfter: fields.notifyOnMissedTasksAfter,
      followupOn: fields.followupOn,
    });
  },
};

const gateCommand = {
  preparePayload(fields = {}) {
    const invalid = required("gate", fields, ["name", "type"]);
    if (invalid) return invalid;
    return ready("gate", "gym/gate", {
      gateName: fields.name,
      type: fields.type,
      openingSchedule: fields.openingSchedule,
    });
  },
};

const courtCommand = {
  preparePayload(fields = {}, context = {}) {
    const invalid = required("court", fields, ["name"]);
    if (invalid) return invalid;
    return ready("court", "court", {
      name: fields.name,
      type:
        fields.type ||
        (context.gymType === "sportClub" ? "Tennis" : "Therapy"),
      dateIntervals: Array.isArray(fields.dateIntervals)
        ? fields.dateIntervals
        : [],
      isRestricted: hasValue(fields.isRestricted)
        ? fields.isRestricted
        : false,
      isPublished: hasValue(fields.isPublished) ? fields.isPublished : true,
    });
  },
};

const appointmentCommand = {
  preparePayload(fields = {}) {
    const invalid = required("appointment", fields, [
      "name",
      "duration",
      "trainers",
      "packages",
    ]);
    if (invalid) return invalid;
    if (!Array.isArray(fields.trainers) || !fields.trainers.length) {
      return needsForm(
        "appointment",
        "missing_required_fields",
        "The manual appointment form requires at least one trainer.",
        { fields: ["trainers"] }
      );
    }
    if (!Array.isArray(fields.packages)) {
      return needsForm(
        "appointment",
        "invalid_packages",
        "Appointment packages must retain the manual form's array shape."
      );
    }

    const packages = fields.packages.map((item) => {
      const id = lookupId(item);
      const count = item && (item.count || parseInt(item.sessionCount, 10));
      return { id, count };
    });
    if (packages.some((item) => !hasValue(item.id) || !hasValue(item.count))) {
      return needsForm(
        "appointment",
        "incomplete_package_lookup",
        "Each appointment package needs the ID and session count supplied by the form lookup."
      );
    }
    const trainers = fields.trainers
      .map((item) => lookupId(item))
      .filter((id) => hasValue(id));
    if (!trainers.length) {
      return needsForm(
        "appointment",
        "incomplete_trainer_lookup",
        "Each appointment trainer needs an ID from the form lookup.",
        { fields: ["trainers"] }
      );
    }

    return ready("appointment", "appointment", {
      displayName: "",
      image: "",
      price: fields.price !== undefined && fields.price !== null ? fields.price : "",
      description: fields.description || "",
      noShowDeduction: Boolean(fields.noShowDeduction),
      allowOnlineBooking: Boolean(fields.allowOnlineBooking),
      allowCancelation: Boolean(fields.allowCancelation),
      allowUnpaidBooking: Boolean(fields.allowUnpaidBooking),
      court: fields.court || null,
      payRates: null,
      coachingType: fields.coachingType || null,
      name: fields.name,
      duration: fields.duration,
      trainers,
      packages,
      payrateId:
        Array.isArray(fields.payRates) && fields.payRates.length
          ? fields.payRates[0].id
          : null,
      appointmentType: lookupId(fields.appointmentType),
    });
  },
};

const baseCommands = {
  member: memberCommand,
  package: scheduledCommands.package,
  lead: simpleCommands.lead,
  product: simpleCommands.product,
  instructor: simpleCommands.instructor,
  staff: staffCommand,
  workout: workoutCommand,
  followup: followupCommand,
  gate: gateCommand,
  court: courtCommand,
  appointment: appointmentCommand,
  class: scheduledCommands.class,
  workshop: scheduledCommands.workshop,
  sale: saleCommand,
};

const withExecute = (command, injectedApi) => ({
  ...command,
  async execute(fields = {}, context = {}) {
    const prepared = command.preparePayload(fields, context);
    if (prepared.outcome !== "ready") return prepared;

    const api = context.api || injectedApi;
    if (typeof api !== "function") {
      return needsForm(
        prepared.type,
        "api_not_injected",
        "A create API function must be injected before this command can execute."
      );
    }

    const response = await api(
      prepared.endpoint,
      prepared.payload,
      prepared.method
    );
    return {
      outcome: "executed",
      type: prepared.type,
      endpoint: prepared.endpoint,
      method: prepared.method,
      payload: prepared.payload,
      response,
    };
  },
});

export const createCommands = ({ api } = {}) =>
  CREATE_COMMAND_TYPES.reduce((commands, type) => {
    commands[type] = withExecute(baseCommands[type], api);
    return commands;
  }, {});

export const getCreateCommand = (type, dependencies) =>
  createCommands(dependencies)[type];

export default createCommands;
